import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import '../../pages.css';

// Parse an .xlsx/.csv file into [{ username, password }]
async function parseSpreadsheet(file) {
  const mod = await import('xlsx');
  const XLSX = mod.default || mod;
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
  const users = [];
  rows.forEach((row, i) => {
    if (!row || !row.length) return;
    const first = String(row[0] || '').toLowerCase().trim();
    if (i === 0 && (first === 'username' || first === '用户名' || first.includes('user'))) return;
    const username = String(row[0] || '').trim();
    const password = row[1] != null ? String(row[1]).trim() : '';
    if (username) users.push({ username, password });
  });
  return users;
}

async function downloadTemplate() {
  const mod = await import('xlsx');
  const XLSX = mod.default || mod;
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([
    ['username', 'password'],
    ['student01', ''],
    ['student02', ''],
  ]);
  XLSX.utils.book_append_sheet(wb, ws, 'users');
  XLSX.writeFile(wb, 'student-template.xlsx');
}

export default function UserManager() {
  const [users, setUsers] = useState([]);
  const [count, setCount] = useState(1);
  const [usernames, setUsernames] = useState('');
  const [result, setResult] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const data = await api.adminUsers();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message || '用户列表加载失败');
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  async function onCreate(e) {
    e.preventDefault();
    setError('');
    setResult(null);
    setSkipped([]);
    setBusy(true);
    try {
      const names = usernames.split(',').map((s) => s.trim()).filter(Boolean);
      let payload;
      if (names.length > 0) {
        payload = names.map((n) => ({ username: n }));
      } else {
        const n = Math.max(1, Math.min(50, Number(count) || 1));
        const rand = () => `student${Math.random().toString(36).slice(2, 8)}`;
        payload = Array.from({ length: n }, () => ({ username: rand() }));
      }
      const res = await api.adminCreateUsers(payload);
      setResult(res.results);
      setSkipped(res.skipped || []);
      setUsernames('');
      loadUsers();
    } catch (err) {
      setError(err.message || '创建失败');
    } finally {
      setBusy(false);
    }
  }

  async function onUploadExcel(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setResult(null);
    setSkipped([]);
    setBusy(true);
    try {
      const parsed = await parseSpreadsheet(file);
      if (parsed.length === 0) { setError('文件中没有找到用户数据（需 username 列）'); return; }
      if (parsed.length > 50) { setError('一次最多 50 个用户，请分批导入'); return; }
      const res = await api.adminCreateUsers(parsed);
      setResult(res.results);
      setSkipped(res.skipped || []);
      loadUsers();
    } catch (err) {
      setError(err.message || '导入失败');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(username) {
    if (!window.confirm('删除该用户？')) return;
    try {
      await api.adminDeleteUser(username);
      setUsers((prev) => prev.filter((u) => u.username !== username));
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="admin-section">
      <h2>批量导入用户（Excel）</h2>
      <div className="toolbar">
        <button className="btn" onClick={downloadTemplate}>下载模板</button>
        <label className="btn" style={{ cursor: 'pointer' }}>
          上传 Excel
          <input type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={onUploadExcel} />
        </label>
      </div>
      <p className="muted">模板含两列：username（用户名）、password（密码，留空则自动生成随机密码）。重复的用户名会自动跳过。</p>

      <h2>或手动创建</h2>
      <form onSubmit={onCreate}>
        <div className="field-row">
          <label>批量数量（随机用户名）</label>
          <input type="number" min="1" max="50" value={count} onChange={(e) => setCount(e.target.value)} />
        </div>
        <div className="field-row">
          <label>指定用户名（逗号分隔，可留空）</label>
          <input value={usernames} onChange={(e) => setUsernames(e.target.value)} placeholder="例如：alice,bob,carol" />
        </div>
        <p className="muted">留空并填数量 = 批量随机用户名；填写用户名 = 按指定用户名创建。</p>
        {error && <p className="error">{error}</p>}
        <button className="btn" type="submit" disabled={busy}>{busy ? '创建中…' : '创建'}</button>
      </form>

      {result && result.length > 0 && (
        <div>
          <h3>新用户凭据（请妥善保存，仅显示一次）</h3>
          <div className="credentials-box">
            {result.map((r) => `用户名: ${r.username}  密码: ${r.password}`).join('\n')}
          </div>
        </div>
      )}

      {skipped && skipped.length > 0 && (
        <div>
          <h3>已跳过 ({skipped.length})</h3>
          <ul className="muted">
            {skipped.map((s, i) => (
              <li key={i}>
                {s.username} — {s.reason === 'duplicate' ? '用户名已存在' : s.reason === 'invalid' ? '用户名格式无效' : '创建失败'}
              </li>
            ))}
          </ul>
        </div>
      )}

      <h2>用户列表 ({users.length})</h2>
      <table className="admin-table">
        <thead><tr><th>用户名</th><th>角色</th><th>创建时间</th><th></th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td>{u.username}</td>
              <td>{u.role || 'member'}</td>
              <td>{u.createdAt ? new Date(u.createdAt).toLocaleString() : ''}</td>
              <td>
                {u.role === 'admin' ? (
                  <span className="muted">受保护</span>
                ) : (
                  <button className="btn secondary" onClick={() => onDelete(u.username)}>删除</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
